import type { PullRequestSummary } from './pull-request';

export type PullRequestActor = {
  login: string;
  avatarUrl: string | null;
};

export type PullRequestCheck = {
  id: string;
  name: string;
  status: 'queued' | 'in_progress' | 'completed' | 'unknown';
  conclusion:
    | 'success'
    | 'failure'
    | 'neutral'
    | 'cancelled'
    | 'skipped'
    | 'timed_out'
    | 'action_required'
    | 'stale'
    | 'unknown'
    | null;
  detailsUrl: string | null;
  title: string | null;
  summary: string | null;
  startedAt: string | null;
  completedAt: string | null;
};

export type PullRequestReaction = {
  /** Unicode emoji for the reaction (e.g. 👍). */
  emoji: string;
  count: number;
};

export type PullRequestReactionContent =
  | 'THUMBS_UP'
  | 'THUMBS_DOWN'
  | 'LAUGH'
  | 'HOORAY'
  | 'CONFUSED'
  | 'HEART'
  | 'ROCKET'
  | 'EYES';

export type PullRequestCommit = {
  oid: string;
  headline: string;
  authoredAt: string | null;
  author: PullRequestActor;
};

export type PullRequestFeedback = {
  id: string;
  kind: 'review' | 'comment' | 'inline';
  author: PullRequestActor;
  body: string;
  state: 'approved' | 'changes_requested' | 'commented' | 'dismissed' | null;
  path: string | null;
  line: number | null;
  diffHunk?: string | null;
  url: string | null;
  createdAt: string | null;
  reactions: readonly PullRequestReaction[];
};

export type PullRequestReviewComment = PullRequestFeedback & {
  kind: 'inline';
  replyToId: string | null;
};

export type PullRequestReviewThread = {
  id: string;
  path: string;
  line: number | null;
  startLine: number | null;
  side: 'left' | 'right' | null;
  isResolved: boolean;
  isOutdated: boolean;
  viewerCanResolve: boolean;
  viewerCanUnresolve: boolean;
  comments: readonly PullRequestReviewComment[];
};

/**
 * Maps a provider reaction identifier — REST keys (`+1`, `heart`…) or GraphQL
 * `reactionGroups` content (`THUMBS_UP`, `HEART`…) — to its Unicode emoji.
 */
export function reactionEmoji(content: string): string | null {
  const map: Record<string, string> = {
    '+1': '👍',
    THUMBS_UP: '👍',
    '-1': '👎',
    THUMBS_DOWN: '👎',
    laugh: '😄',
    LAUGH: '😄',
    hooray: '🎉',
    HOORAY: '🎉',
    confused: '😕',
    CONFUSED: '😕',
    heart: '❤️',
    HEART: '❤️',
    rocket: '🚀',
    ROCKET: '🚀',
    eyes: '👀',
    EYES: '👀',
  };
  return map[content] ?? null;
}

/** Normalizes a REST `reactions` summary object into non-empty reaction counts. */
export function restReactions(value: unknown): PullRequestReaction[] {
  if (typeof value !== 'object' || value === null) return [];
  const record = value as Record<string, unknown>;
  const out: PullRequestReaction[] = [];
  for (const [key, raw] of Object.entries(record)) {
    const emoji = reactionEmoji(key);
    const count = typeof raw === 'number' ? raw : 0;
    if (emoji !== null && count > 0) out.push({ emoji, count });
  }
  return out;
}

export type PullRequestDiffLine = {
  kind: 'hunk' | 'context' | 'addition' | 'deletion' | 'meta';
  oldLine: number | null;
  newLine: number | null;
  text: string;
};

export type PullRequestDiffExcerptLine = PullRequestDiffLine & { isTarget: boolean };

/** Converts a provider review comment's diff hunk into displayable numbered lines. */
export function pullRequestDiffLines(diffHunk: string): PullRequestDiffLine[] {
  let oldLine = 0;
  let newLine = 0;
  return diffHunk.split('\n').map((text) => {
    const header = text.match(/^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/);
    if (header !== null) {
      oldLine = Number(header[1]);
      newLine = Number(header[2]);
      return { kind: 'hunk' as const, oldLine: null, newLine: null, text };
    }
    if (text.startsWith('+')) {
      const line = { kind: 'addition' as const, oldLine: null, newLine, text };
      newLine += 1;
      return line;
    }
    if (text.startsWith('-')) {
      const line = { kind: 'deletion' as const, oldLine, newLine: null, text };
      oldLine += 1;
      return line;
    }
    if (text.startsWith(' ')) {
      const line = { kind: 'context' as const, oldLine, newLine, text };
      oldLine += 1;
      newLine += 1;
      return line;
    }
    return { kind: 'meta' as const, oldLine: null, newLine: null, text };
  });
}

/** Selects the annotated range plus a small amount of surrounding context. */
export function pullRequestDiffExcerpt(
  diffHunk: string,
  annotation: {
    line: number | null;
    startLine: number | null;
    side: 'left' | 'right' | null;
  },
  context = 0,
): PullRequestDiffExcerptLine[] {
  const lines = pullRequestDiffLines(diffHunk).filter((line) => line.kind !== 'hunk');
  const fallback = (): PullRequestDiffExcerptLine[] =>
    lines.slice(-Math.max(1, context * 2 + 1)).map((line) => ({ ...line, isTarget: false }));
  const endLine = annotation.line ?? annotation.startLine;
  if (endLine === null) return fallback();
  const startLine = Math.min(annotation.startLine ?? endLine, endLine);
  const targetLine = (line: PullRequestDiffLine): number | null =>
    annotation.side === 'left' ? line.oldLine : (line.newLine ?? line.oldLine);
  const targetIndexes = lines.flatMap((line, index) => {
    const number = targetLine(line);
    return number !== null && number >= startLine && number <= endLine ? [index] : [];
  });
  if (targetIndexes.length === 0) return fallback();
  const first = targetIndexes[0] ?? 0;
  const last = targetIndexes.at(-1) ?? first;
  const from = Math.max(0, first - context);
  const to = Math.min(lines.length, last + context + 1);
  const targets = new Set(targetIndexes);
  return lines.slice(from, to).map((line, offset) => ({
    ...line,
    isTarget: targets.has(from + offset),
  }));
}
export type PullRequestCockpit = {
  pullRequest: PullRequestSummary;
  author: PullRequestActor;
  headSha: string;
  baseSha: string;
  mergeable: 'mergeable' | 'conflicting' | 'unknown';
  autoMergeEnabled: boolean;
  reviewDecision: 'approved' | 'changes_requested' | 'review_required' | 'unknown';
  additions: number;
  deletions: number;
  changedFiles: number;
  commits: number;
  commitItems: readonly PullRequestCommit[];
  labels: readonly string[];
  requestedReviewers: readonly PullRequestActor[];
  checks: readonly PullRequestCheck[];
  feedback: readonly PullRequestFeedback[];
  reviewThreads: readonly PullRequestReviewThread[];
  /** Reactions on the pull request description (its opening post). */
  reactions: readonly PullRequestReaction[];
};

export type PullRequestIssue =
  | { key: string; type: 'failing_check'; check: PullRequestCheck }
  | { key: string; type: 'review_thread'; thread: PullRequestReviewThread }
  | {
      key: string;
      type: 'merge_conflict';
      baseBranch: string;
      headBranch: string;
      headSha: string;
    };

export type PullRequestMutation = {
  action:
    | 'close'
    | 'comment'
    | 'disable_auto_merge'
    | 'draft'
    | 'edit'
    | 'enable_auto_merge'
    | 'merge'
    | 'ready'
    | 'react_comment'
    | 'reply_thread'
    | 'reopen'
    | 'request_reviewers'
    | 'resolve_thread'
    | 'submit_review'
    | 'update_branch';
  expectedHeadSha: string;
  title?: string;
  body?: string;
  decision?: 'approve' | 'comment' | 'request_changes';
  reviewers?: string[];
  method?: 'merge' | 'squash';
  threadId?: string;
  commentId?: string;
  reaction?: PullRequestReactionContent;
};

export function unresolvedPullRequestThreads(
  cockpit: PullRequestCockpit,
): PullRequestReviewThread[] {
  return (cockpit.reviewThreads ?? []).filter((thread) => !thread.isResolved && !thread.isOutdated);
}

function feedbackIdentity(feedback: PullRequestFeedback): string {
  if (feedback.url !== null && feedback.url.trim().length > 0) return `url:${feedback.url}`;
  return [
    feedback.author.login,
    feedback.path ?? '',
    feedback.line ?? '',
    feedback.createdAt ?? '',
    feedback.body.trim(),
  ].join('\n');
}

/**
 * Builds the visible PR conversation without hiding resolved annotations or
 * duplicating comments returned by both provider GraphQL and REST endpoints.
 */
export function visiblePullRequestFeedback(cockpit: PullRequestCockpit): {
  threads: PullRequestReviewThread[];
  standalone: PullRequestFeedback[];
} {
  const threads = (cockpit.reviewThreads ?? []).filter((thread) => thread.comments.length > 0);
  const threadedFeedbackIds = new Set(
    threads.flatMap((thread) => thread.comments.map(feedbackIdentity)),
  );
  const standalone = (cockpit.feedback ?? []).filter((feedback) => {
    if (feedback.kind === 'inline' && threadedFeedbackIds.has(feedbackIdentity(feedback))) {
      return false;
    }
    // Some providers create an empty review event for each submitted batch of inline
    // annotations. The annotations are represented by the thread comments.
    return !(
      feedback.kind === 'review' &&
      feedback.state === 'commented' &&
      feedback.body.trim().length === 0
    );
  });
  return { threads, standalone };
}

export function pullRequestIssues(cockpit: PullRequestCockpit): PullRequestIssue[] {
  const checks: PullRequestIssue[] = failedPullRequestChecks(cockpit).map((check) => ({
    key: `check:${check.id}`,
    type: 'failing_check',
    check,
  }));
  const threads: PullRequestIssue[] = unresolvedPullRequestThreads(cockpit).map((thread) => ({
    key: `thread:${thread.id}`,
    type: 'review_thread',
    thread,
  }));
  const conflict: PullRequestIssue[] =
    cockpit.mergeable === 'conflicting'
      ? [
          {
            key: `conflict:${cockpit.headSha}`,
            type: 'merge_conflict',
            baseBranch: cockpit.pullRequest.baseBranch,
            headBranch: cockpit.pullRequest.headBranch,
            headSha: cockpit.headSha,
          },
        ]
      : [];
  return [...conflict, ...checks, ...threads];
}

export function pullRequestIssueText(issue: PullRequestIssue): string {
  if (issue.type === 'failing_check') {
    const output = [issue.check.title, issue.check.summary].filter(Boolean).join('\n');
    return [
      `Failing CI check: ${issue.check.name}`,
      `Status: ${issue.check.conclusion ?? issue.check.status}`,
      output,
      issue.check.detailsUrl === null ? '' : `Details: ${issue.check.detailsUrl}`,
    ]
      .filter(Boolean)
      .join('\n');
  }
  if (issue.type === 'review_thread') {
    const comments = issue.thread.comments
      .map((comment) => `${comment.author.login}: ${comment.body.trim()}`)
      .join('\n');
    return [
      `Unresolved review thread at ${issue.thread.path}${issue.thread.line === null ? '' : `:${issue.thread.line}`}`,
      comments,
    ]
      .filter(Boolean)
      .join('\n');
  }
  return `Merge conflict between ${issue.baseBranch} and ${issue.headBranch} at ${issue.headSha}.`;
}

export function pullRequestIssuePrompt(
  cockpit: PullRequestCockpit,
  issues: readonly PullRequestIssue[],
): string {
  const kinds = new Set(issues.map((issue) => issue.type));
  const tasks = [
    kinds.has('merge_conflict') ? 'resolve the merge conflicts against the latest base branch' : '',
    kinds.has('failing_check') ? 'fix the selected failing CI checks' : '',
    kinds.has('review_thread') ? 'address every selected unresolved review thread' : '',
  ].filter(Boolean);
  return [
    `Work on pull request #${cockpit.pullRequest.number} (${cockpit.pullRequest.headBranch} -> ${cockpit.pullRequest.baseBranch}) and ${tasks.join(', ')}.`,
    'First verify the current branch, pull request head SHA, provider state, and linked CI details. Do not guess when access is missing or a comment is outdated; explain the exact blocker instead.',
    'Keep the changes minimal, run the narrowest relevant verification, then commit and push the fix to the pull request branch. Report how each attached item was handled.',
  ].join('\n\n');
}

export function pullRequestWatchPrompt(cockpit: PullRequestCockpit): string {
  return [
    `Watch the CI checks for pull request #${cockpit.pullRequest.number} at head ${cockpit.headSha} until they reach a terminal state.`,
    'Verify that the current branch and pull request head still match before changing anything. If a check fails, inspect its real logs, make the smallest relevant fix, run focused verification, then commit and push. If the head changes or access is unavailable, stop and report the blocker instead of acting on stale results.',
  ].join('\n\n');
}

export function pullRequestFixDisabledReason(
  cockpit: PullRequestCockpit,
  currentBranch: string | null,
  enforceBranch: boolean,
): string | null {
  if (cockpit.pullRequest.state !== 'open')
    return 'Fix is available only for an open pull request.';
  if (
    enforceBranch &&
    (currentBranch === null || currentBranch.trim() !== cockpit.pullRequest.headBranch.trim())
  ) {
    return `Switch back to ${cockpit.pullRequest.headBranch} to use Fix.`;
  }
  if (cockpit.headSha.trim().length === 0) return 'Refresh the pull request before using Fix.';
  return null;
}

export function failedPullRequestChecks(cockpit: PullRequestCockpit): PullRequestCheck[] {
  return cockpit.checks.filter(
    (check) =>
      check.conclusion === 'failure' ||
      check.conclusion === 'timed_out' ||
      check.conclusion === 'action_required',
  );
}

export function pendingPullRequestChecks(cockpit: PullRequestCockpit): PullRequestCheck[] {
  return cockpit.checks.filter(
    (check) => check.status !== 'completed' || check.conclusion === null,
  );
}

export function actionablePullRequestFeedback(cockpit: PullRequestCockpit): PullRequestFeedback[] {
  if ((cockpit.reviewThreads?.length ?? 0) > 0) {
    return unresolvedPullRequestThreads(cockpit).flatMap((thread) => thread.comments);
  }
  return (cockpit.feedback ?? []).filter(
    (feedback) => feedback.state === 'changes_requested' || feedback.kind === 'inline',
  );
}

/** Mirrors Codex's guarded PR flow: CI and mergeability must settle first. */
export function pullRequestMergeDisabledReason(cockpit: PullRequestCockpit): string | null {
  if (cockpit.pullRequest.state === 'merged') return 'This pull request is already merged.';
  if (cockpit.pullRequest.state === 'closed') return 'Reopen this pull request before merging.';
  if (cockpit.pullRequest.state === 'draft') {
    return 'Mark this pull request ready for review before merging.';
  }
  if (cockpit.mergeable === 'conflicting') return 'Resolve merge conflicts before merging.';
  if (cockpit.mergeable === 'unknown') {
    return 'The source-control provider is still checking whether this can be merged.';
  }
  if (failedPullRequestChecks(cockpit).length > 0) return 'Fix failing checks before merging.';
  if (pendingPullRequestChecks(cockpit).length > 0) {
    return 'Wait for checks to finish before merging.';
  }
  return null;
}

export function pullRequestChecksPrompt(cockpit: PullRequestCockpit): string {
  const failed = failedPullRequestChecks(cockpit);
  const details = failed
    .map((check) => {
      const output = [check.title, check.summary].filter(Boolean).join('\n');
      return `### ${check.name}\nStatus: ${check.conclusion ?? check.status}${output ? `\n${output}` : ''}${
        check.detailsUrl ? `\nDetails: ${check.detailsUrl}` : ''
      }`;
    })
    .join('\n\n');
  return [
    `Fix the failing CI checks for pull request #${cockpit.pullRequest.number}.`,
    'Inspect the repository and reproduce failures locally where possible. Keep the fix scoped, run relevant verification, and report evidence.',
    details ||
      'The source-control provider did not expose a failure summary. Inspect the repository CI configuration and current branch.',
  ].join('\n\n');
}

export function pullRequestFeedbackPrompt(
  cockpit: PullRequestCockpit,
  feedback: readonly PullRequestFeedback[] = actionablePullRequestFeedback(cockpit),
): string {
  const details = feedback
    .map((item) => {
      const location =
        item.path === null ? '' : ` (${item.path}${item.line === null ? '' : `:${item.line}`})`;
      return `- ${item.author.login}${location}: ${item.body.trim()}`;
    })
    .join('\n');
  return [
    `Address the selected review feedback for pull request #${cockpit.pullRequest.number}.`,
    'Keep changes minimal, verify the result, and explain how each comment was addressed.',
    details ||
      '- No actionable feedback was selected. Review the pull request and identify what still needs attention.',
  ].join('\n\n');
}

/**
 * Streamdown does not execute raw HTML. Provider comments commonly use
 * details/summary wrappers, so keep their content readable as Markdown instead
 * of exposing the tags verbatim.
 */
export function providerCommentMarkdown(value: string): string {
  return value
    .replace(
      /<summary\b[^>]*>([\s\S]*?)<\/summary>/gi,
      (_match, summary: string) => `**${summary.trim()}**\n\n`,
    )
    .replace(/<\/?details\b[^>]*>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n');
}
